import React, { useState, useEffect } from 'react';
import { Camera, X, User, ChevronLeft, Check, Plus, UserPlus, Link, QrCode, Shield, MessageSquare } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import BottomNavigation from '../components/BottomNavigation';

/**
 * QR Kod Tarayıcı Ekranı
 * Başka bir kullanıcının QR kodunu taramak için kullanılır
 */
const QRScannerScreen = () => {
  const { navigateToScreen } = useUI();
  
  // Tarama durumları
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(true);
  const [cameraPermission, setCameraPermission] = useState(true); // Gerçekte izin kontrolü yapılmalı
  const [flashActive, setFlashActive] = useState(false);
  
  // Kamera izinleri - gerçek uygulamada navigator.mediaDevices.getUserMedia kullanılır
  useEffect(() => {
    // Simüle edilmiş kamera izni sorgusu
    const checkCameraPermission = async () => {
      try {
        // Gerçek kamera erişimi kodu burada olacak
        // Şimdilik izin verilmiş gibi varsayalım
        setCameraPermission(true);
      } catch (error) {
        console.error('Kamera erişimi reddedildi:', error);
        setCameraPermission(false);
      }
    };
    
    checkCameraPermission();
  }, []);
  
  // Tarama işlemini simüle eden örnek veri
  const sampleUserData = {
    name: "Ömer Köse",
    username: "@omerkose",
    profileImage: null,
    verification: "verified",
    connections: 342,
    personalInfo: {
      bloodType: "0 Rh+",
      languages: ["Türkçe", "English", "Deutsch"],
      location: "İstanbul, Türkiye"
    }
  };
  
  // QR tarama işlemini simüle eden fonksiyon
  const handleScan = () => {
    setIsScanning(false);
    setScanResult(sampleUserData);
  };
  
  // Tarama ekranına geri dönme
  const resetScan = () => {
    setIsScanning(true);
    setScanResult(null);
  };

  // Kamera izni reddedilmiş durumu
  if (!cameraPermission) {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        <header className="bg-white p-4 shadow flex items-center">
          <button onClick={() => navigateToScreen('dashboard')} className="mr-2">
            <ChevronLeft size={20} />
          </button>
          <span className="font-bold text-lg">QR Okutucu</span>
        </header>
        
        <main className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Camera size={64} className="text-gray-400 mb-4" />
          <h2 className="text-xl font-bold mb-2">Kamera İzni Gerekli</h2>
          <p className="text-gray-600 mb-4">
            QR kodları taramak için kameranıza erişim izni vermeniz gerekiyor.
          </p>
          <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg">
            İzin Ver
          </button>
        </main>
        
        <BottomNavigation activeItem="qr" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Top Header */}
      <header className="bg-black p-4 shadow flex justify-between items-center">
        <button 
          onClick={() => navigateToScreen('dashboard')} 
          className="text-white"
        >
          <X size={24} />
        </button>
        <span className="font-bold text-lg text-white">QR Okutucu</span>
        <button 
          className="text-white"
          onClick={() => setFlashActive(!flashActive)}
        >
          {flashActive ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6M10 22h4M11 4h2M18 6h.01M6 6h.01M18 14h.01M6 14h.01M14 6h.01M14 14h.01M6 10h12M13 2h-2v4M16 2h-4v4M11 18v4M11 14v4" strokeWidth="1.5" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6M10 22h4M9 2h6M11 6V2M15 6V2M19 9v6M22 10v4M18 14h.01M18 6h.01M6 6h.01M6 14h.01M2 10v4M6 10h12M14 6h.01M14 14h.01" strokeWidth="1.5" />
            </svg>
          )}
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 relative overflow-hidden">
        {isScanning ? (
          /* Tarama Ekranı */
          <div className="flex flex-col h-full">
            <div className="relative flex-1 bg-black">
              {/* Kamera önizleme (gerçek bir kamera yerine bir placeholder gösteriyoruz) */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Camera size={48} className="text-white opacity-20" />
              </div>
              
              {/* QR tarama çerçevesi */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-64 h-64 border-2 border-white rounded-lg">
                  <div className="absolute -top-2 -left-2 h-8 w-8 border-l-2 border-t-2 border-white"></div>
                  <div className="absolute -top-2 -right-2 h-8 w-8 border-r-2 border-t-2 border-white"></div>
                  <div className="absolute -bottom-2 -left-2 h-8 w-8 border-l-2 border-b-2 border-white"></div>
                  <div className="absolute -bottom-2 -right-2 h-8 w-8 border-r-2 border-b-2 border-white"></div>
                </div>
              </div>
              
              {/* Scan Animation - Animasyonlu tarayıcı çizgisi */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 overflow-hidden">
                  <div className="w-full h-2 bg-indigo-500 opacity-70 absolute animate-bounce-slow" style={{ top: '50%' }}></div>
                </div>
              </div>
              
              {/* Taramayı simüle etmek için eklenen tıklama alanı */}
              <button 
                className="absolute inset-0 w-full h-full"
                onClick={handleScan}
                aria-label="Scan QR Code"
              />
            </div>
            
            <div className="p-4 bg-black">
              <p className="text-center text-white">
                QR kodu taramak için kamerayı kodun üzerine doğrultun
              </p>
            </div>
          </div>
        ) : (
          /* Tarama Sonucu Ekranı */
          <div className="flex flex-col h-full bg-white">
            {scanResult && (
              <>
                <div className="p-6 flex flex-col items-center justify-center bg-indigo-600 text-white">
                  <div className="mb-4 w-24 h-24 bg-white rounded-full flex items-center justify-center">
                    {scanResult.profileImage ? (
                      <img 
                        src={scanResult.profileImage} 
                        alt={scanResult.name} 
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User size={64} className="text-indigo-600" />
                    )}
                  </div>
                  
                  <h2 className="text-xl font-bold">{scanResult.name}</h2>
                  <p className="text-indigo-200">{scanResult.username}</p>
                  
                  {scanResult.verification === "verified" && (
                    <div className="mt-2 px-3 py-1 bg-green-500 text-white rounded-full text-xs flex items-center">
                      <Check size={12} className="mr-1" />
                      Doğrulanmış Kimlik
                    </div>
                  )}
                </div>
                
                <div className="p-4 flex-1">
                  <div className="mb-6 flex justify-around">
                    <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg shadow-md">
                      Profili Gör
                    </button>
                    <button className="bg-white border border-indigo-600 text-indigo-600 px-6 py-2 rounded-lg shadow-sm flex items-center">
                      <Plus size={16} className="mr-1" />
                      Bağlantı Ekle
                    </button>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kan grubu</span>
                      <span className="font-medium">{scanResult.personalInfo.bloodType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Diller</span>
                      <span className="font-medium">{scanResult.personalInfo.languages.join(", ")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Konum</span>
                      <span className="font-medium">{scanResult.personalInfo.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bağlantılar</span>
                      <span className="font-medium">{scanResult.connections}</span>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <button 
                      className="w-full flex items-center justify-center space-x-2 py-3 bg-gray-200 text-gray-700 rounded-lg" 
                      onClick={resetScan}
                    >
                      <Camera size={20} />
                      <span>Yeni QR Tara</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* Bottom Navigation - Sadece tarama sonrası göster */}
      {!isScanning && <BottomNavigation activeItem="qr" />}
      
      {/* Animasyon stilleri */}
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(-10px);
          }
          50% {
            transform: translateY(170px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default QRScannerScreen;
